import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown, PencilIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputFieldDebounce from "./ui/InputFieldDebounce";
import SelectField from "./ui/SelectField";
import { getCountries } from "../api/AddressApi";

interface AddressGroupSelectProps {
  type: "INLINE" | "FORM" | "GRID";
  street: string;
  onStreetChange: (street: string) => void;
  city: any;
  onCityChange: (city: any) => void;
  state: any;
  onStateChange: (state: any) => void;
  country: any;
  onCountryChange: (country: any) => void;
  placeholder?: string;
  testIdPrefix?: string;
}

const AddressGroupSelect = ({
  type,
  street,
  onStreetChange,
  city,
  onCityChange,
  state,
  onStateChange,
  country,
  onCountryChange,
  placeholder,
  testIdPrefix,
}: AddressGroupSelectProps) => {
  const { t } = useTranslation();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);

  const { data: countries } = useQuery({
    queryKey: ["country"],
    queryFn: () => getCountries(""),
  });

  const renderTarget = (isOpen) => {
    const parts: string[] = [];
    if (street) parts.push(street);
    if (city && city.label) parts.push(city.label);
    if (state && state.label) parts.push(state.label);
    if (country && country.label) parts.push(country.label);
    if (type === "INLINE") {
      return (
        <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
          {parts.length > 0 ? (
            <div className="truncate w-full text-xs font-medium leading-4">{parts.join(", ")}</div>
          ) : (
            <div className="w-full text-xs font-medium leading-4 text-gray-400 cursor-default">
              {placeholder}
            </div>
          )}
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
          </div>
        </div>
      );
    } else if (type === "FORM") {
      return (
        <div
          className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
            isOpen ? "ring-4 ring-blue-100 border border-blue-600" : "border border-gray-200"
          }`}
        >
          {parts.length > 0 ? (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm font-medium leading-4 truncate">{parts.join(", ")}</div>
            </div>
          ) : (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
            </div>
          )}
          <div className="shrink-0 flex items-center flex-nowrap gap-4">
            <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      );
    } else if (type === "GRID") {
      return (
        <div className="w-full cursor-pointer">
          {parts.length > 0 ? (
            <div className="w-full text-sm font-medium text-gray-800">{parts.join(", ")}</div>
          ) : (
            <div className="w-full text-left leading-4 text-gray-500 cursor-default">
              {placeholder}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  let popoverPanelClassName = "";
  if (type === "INLINE") {
  } else if (type === "FORM") {
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    popoverPanelClassName = "mt-1";
  }

  return (
    <Popover>
      {({ open }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useLayoutEffect(() => {
          if (!open) return;
          if (!popoverButtonRef.current || !popoverPanelRef.current) return;
          const buttonWidth = popoverButtonRef.current.offsetWidth;
          const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
          setPanelWidth(Math.max(buttonWidth, panelNaturalWidth));
        }, [open]);

        return (
          <>
            <PopoverButton
              data-testid={testIdPrefix ? `${testIdPrefix}-trigger` : undefined}
              ref={popoverButtonRef}
              as="div"
              onClick={(e) => {
                if (open) {
                  e.preventDefault();
                }
              }}
            >
              {renderTarget(open)}
            </PopoverButton>
            <PopoverPanel
              ref={popoverPanelRef}
              anchor="bottom start"
              style={{ width: panelWidth ?? "auto" }}
              className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
            >
              <div className="space-y-2">
                <div>
                  <InputFieldDebounce
                    testId={testIdPrefix ? `${testIdPrefix}-street-input` : undefined}
                    label={t("Address")}
                    placeholder={t("Set address...")}
                    value={street}
                    onChange={(value) => onStreetChange(value)}
                  />
                </div>
                <div>
                  <InputFieldDebounce
                    testId={testIdPrefix ? `${testIdPrefix}-city-input` : undefined}
                    label={t("City")}
                    placeholder={t("Set city...")}
                    value={city?.label || ""}
                    onChange={(value) => onCityChange({ label: value })}
                  />
                </div>
                <div>
                  <InputFieldDebounce
                    testId={testIdPrefix ? `${testIdPrefix}-state-input` : undefined}
                    label={t("State")}
                    placeholder={t("Set state...")}
                    value={state?.label || ""}
                    onChange={(value) => onStateChange({ label: value, value: value })}
                  />
                </div>
                <div className="w-full">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    {t("Country")}
                  </label>
                  <SelectField
                    testId={testIdPrefix ? `${testIdPrefix}-country-input` : undefined}
                    menuPlacement="auto"
                    options={countries}
                    value={country}
                    onChange={(selectedOption) => {
                      onCountryChange(selectedOption);
                    }}
                    placeholder={t("Select country...")}
                    isClearable
                    dropdownIndicator={<ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
                  />
                </div>
              </div>
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

export default AddressGroupSelect;
